
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import type { Quote, RepairType } from "@/lib/types";
import { Smartphone, Wrench, Building, Mail, ShieldCheck, Gem, User, Phone } from "lucide-react";
import { useEffect, useState } from "react";

// This interface defines the structure of the data returned by the new public API
interface PublicPart {
    sku: string;
    product: string;
    quality: "Original" | "OLED" | "Incell" | null;
    price_installed: number | null | undefined;
    inStock: boolean;
}


const formSchema = z.object({
  brand: z.string({ required_error: "Por favor selecciona una marca." }),
  otherBrand: z.string().optional(),
  model: z.string({ required_error: "Por favor selecciona un modelo." }),
  otherModel: z.string().optional(),
  repair_type: z.string({
    required_error: "Por favor selecciona un tipo de reparación.",
  }),
  screen_quality: z.string().optional(),
  name: z.string().optional(),
  client_phone: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.brand === 'Otro' && !data.otherBrand) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Por favor ingresa el nombre de la marca.",
            path: ['otherBrand'],
        });
    }
    if (data.model === 'Otro' && !data.otherModel) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Por favor ingresa el nombre del modelo.",
            path: ['otherModel'],
        });
    }
    const isScreenRepairByRepairType = data.repair_type === 'Cambio de pantalla';
    if (isScreenRepairByRepairType && !data.screen_quality) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Por favor selecciona una calidad de pantalla.",
            path: ['screen_quality'],
        });
    }
});

type QuoteFormValues = z.infer<typeof formSchema>;

interface QuoteFormProps {
  onAddQuote: (quote: Omit<Quote, "id" | "tracking_id">) => void;
}

export default function QuoteForm({ onAddQuote }: QuoteFormProps) {
  const { toast } = useToast();
  const [brands, setBrands] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [repairTypes, setRepairTypes] = useState<RepairType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingParts, setIsLoadingParts] = useState(false);
  const [availableParts, setAvailableParts] = useState<PublicPart[]>([]);
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
        brand: "",
        otherBrand: "",
        model: "",
        otherModel: "",
        repair_type: "",
        screen_quality: "",
        name: "",
        client_phone: "",
    }
  });

  useEffect(() => {
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [brandsRes, repairTypesRes] = await Promise.all([
                fetch('/api/brands'),
                fetch('/api/repair-types')
            ]);
            // Add "Otro" option to brands
            const brandsData = await brandsRes.json();
            setBrands([...brandsData, "Otro"]);
            setRepairTypes(await repairTypesRes.json());
        } catch (error) {
            toast({
                title: "Error",
                description: "No se pudieron cargar los datos iniciales.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };
    fetchData();
  }, [toast]);
  
  const brandValue = form.watch('brand');
  const modelValue = form.watch('model');
  const repairTypeValue = form.watch('repair_type');
  const qualityValue = form.watch('screen_quality');
  
  const selectedRepairType = repairTypes.find(rt => rt.name === repairTypeValue);
  const isScreenRepair = selectedRepairType?.name === "Cambio de pantalla";
  
  useEffect(() => {
    const handleBrandChange = async (brand: string) => {
        setIsLoadingModels(true);
        setModels([]);
        if (brand && brand !== 'Otro') {
            try {
                const res = await fetch(`/api/models/${brand}`);
                const data = await res.json();
                setModels([...data, "Otro"]); // Add "Otro" option to models
            } catch (error) {
                 toast({
                    title: "Error",
                    description: "No se pudieron cargar los modelos.",
                    variant: "destructive"
                });
            }
        }
        setIsLoadingModels(false);
    };
    handleBrandChange(brandValue);
  }, [brandValue, toast]);
  
  useEffect(() => {
    form.setValue('screen_quality', '');
    setAvailableParts([]);
    if(brandValue && modelValue && repairTypeValue && brandValue !== 'Otro' && modelValue !== 'Otro') {
        const fetchParts = async () => {
            setIsLoadingParts(true);
            try {
                const query = new URLSearchParams({ brand: brandValue, model: modelValue, repairType: repairTypeValue });
                // *** Use the new PUBLIC API endpoint ***
                const res = await fetch(`/api/quote/parts?${query.toString()}`);
                const data: PublicPart[] = await res.json();
                setAvailableParts(data);
            } catch (error) {
                 toast({
                    title: "Error",
                    description: "No se pudieron cargar las partes para este modelo.",
                    variant: "destructive"
                });
                setAvailableParts([]);
            } finally {
                setIsLoadingParts(false);
            }
        }
        fetchParts();
    }
  }, [brandValue, modelValue, repairTypeValue, form, toast]);


  useEffect(() => {
    const selectedPart = isScreenRepair
      ? availableParts.find(p => p.quality === qualityValue)
      : availableParts.length > 0 ? availableParts[0] : null;

    if (selectedPart && selectedPart.price_installed) {
        setEstimatedPrice(selectedPart.price_installed);
    } else {
        setEstimatedPrice(null);
    }
  }, [qualityValue, availableParts, isScreenRepair]);

  const isManualQuoteNeeded = !isLoadingParts && brandValue && modelValue && repairTypeValue && availableParts.length === 0;

  const onSubmit = async (data: QuoteFormValues) => {
    const finalBrand = data.brand === 'Otro' ? data.otherBrand! : data.brand;
    const finalModel = data.model === 'Otro' ? data.otherModel! : data.model;

    // If it's a manual quote request
    if (isManualQuoteNeeded) {
        if (!data.name || !data.client_phone) {
            toast({ title: "Error", description: "El nombre y teléfono son requeridos para una cotización personalizada.", variant: "destructive" });
            return;
        }
        try {
            const response = await fetch('/api/quote/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    brand: finalBrand,
                    model: finalModel,
                    repair_type: data.repair_type,
                    name: data.name,
                    client_phone: data.client_phone
                }),
            });

            if (!response.ok) throw new Error("Server error");
            
            toast({
                title: "Solicitud Recibida",
                description: "Gracias. Nos pondremos en contacto contigo pronto con una cotización.",
            });
            form.reset();
            setAvailableParts([]);
            setEstimatedPrice(null);

        } catch (error) {
            toast({
                title: "Error",
                description: "No se pudo enviar tu solicitud. Por favor, intenta de nuevo.",
                variant: "destructive"
            });
        }
        return;
    }
    
    // If it's a standard quote
    const quoteData: Omit<Quote, "id" | "tracking_id"> = {
        brand: finalBrand,
        model: finalModel,
        repair_type: data.repair_type,
        screen_quality: data.screen_quality,
        price: estimatedPrice ?? undefined,
    };
    
    onAddQuote(quoteData);

    toast({
        title: "Cotización Agregada",
        description: `${finalBrand} ${finalModel} ha sido añadido a la lista.`,
    });
    
    form.reset();
    setAvailableParts([]);
    setEstimatedPrice(null);
  };

  const handleBrandChange = (brand: string) => {
    form.setValue("brand", brand);
    form.setValue("model", "");
    form.setValue("otherModel", "");
    if (brand !== 'Otro') {
        form.setValue("otherBrand", "");
    }
  };

  const handleModelChange = (model: string) => {
    form.setValue("model", model);
    if (model !== 'Otro') {
        form.setValue("otherModel", "");
    }
  }
  
  const isBrandSelected = !!brandValue;
  const isOtherBrand = brandValue === 'Otro';
  const isOtherModel = modelValue === 'Otro';
  const isPriceAvailable = estimatedPrice !== null;
  const showNoStockMessage = !isLoadingParts && availableParts.length === 0 && brandValue && modelValue && repairTypeValue && brandValue !== 'Otro' && modelValue !== 'Otro';


  return (
    <>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-headline">
          <Wrench className="w-6 h-6 text-primary" />
          Cotizador de Reparación
        </CardTitle>
        <CardDescription>
          Completa los siguientes campos para obtener un estimado del costo de
          reparación.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="brand"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Marca
                  </FormLabel>
                  <Select
                    onValueChange={handleBrandChange}
                    value={field.value}
                    disabled={isLoading}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoading ? "Cargando marcas..." : "Selecciona la marca de tu dispositivo"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {brands.map((brand) => (
                        <SelectItem key={brand} value={brand}>
                          {brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {isOtherBrand && (
                 <FormField
                 control={form.control}
                 name="otherBrand"
                 render={({ field }) => (
                   <FormItem>
                     <FormLabel>Específica la marca</FormLabel>
                     <FormControl>
                       <Input placeholder="Ej. OnePlus" {...field} />
                     </FormControl>
                     <FormMessage />
                   </FormItem>
                 )}
               />
            )}

            <FormField
              control={form.control}
              name="model"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Modelo
                  </FormLabel>
                  <Select
                    onValueChange={handleModelChange}
                    value={field.value}
                    disabled={!isBrandSelected || isOtherBrand || isLoadingModels}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            isLoadingModels ? 'Cargando modelos...' :
                            isOtherBrand ? "Especifica el modelo abajo" :
                            !isBrandSelected
                              ? "Primero selecciona una marca"
                              : "Selecciona el modelo"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {brandValue && !isOtherBrand &&
                        models.map((model) => (
                          <SelectItem key={model} value={model}>
                            {model}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {(isOtherModel || isOtherBrand) && (
                 <FormField
                 control={form.control}
                 name="otherModel"
                 render={({ field }) => (
                   <FormItem>
                     <FormLabel>Específica el modelo</FormLabel>
                     <FormControl>
                       <Input placeholder="Ej. iPhone 16 Pro" {...field} />
                     </FormControl>
                     <FormMessage />
                   </FormItem>
                 )}
               />
            )}

            <FormField
              control={form.control}
              name="repair_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Tipo de Reparación
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isLoading}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoading ? "Cargando..." : "Selecciona el servicio que necesitas"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {repairTypes.map((type) => (
                        <SelectItem key={type.name} value={type.name}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {repairTypeValue && !isScreenRepair && (
                    <FormDescription className="flex items-center gap-2 pt-2 text-primary">
                        <ShieldCheck className="w-4 h-4" />
                        <span>Garantía de 60 días</span>
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {isScreenRepair && (
              <FormField
                control={form.control}
                name="screen_quality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Gem className="w-4 h-4" />
                      Calidad de la Pantalla
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLoadingParts || availableParts.length === 0}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={
                            isLoadingParts ? "Buscando calidades..." :
                            showNoStockMessage || (availableParts.length > 0 && !availableParts.some(p => p.inStock)) ? "No disponible para este modelo" :
                            "Selecciona una calidad"
                          } />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {availableParts.map((part) => (
                          <SelectItem key={part.sku} value={part.quality!} disabled={!part.inStock}>
                             {part.quality} ({part.inStock ? 'Disponible' : 'Agotado'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                     {repairTypeValue && (
                        <FormDescription className="flex items-center gap-2 pt-2 text-primary">
                            <ShieldCheck className="w-4 h-4" />
                            <span>Garantía de 90 días</span>
                        </FormDescription>
                      )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isManualQuoteNeeded && (
                <div className="!mt-8 p-4 border rounded-lg bg-secondary/50 space-y-4">
                    <p className="text-sm font-medium text-center">No encontramos una cotización automática para este modelo. Por favor, déjanos tus datos y te contactaremos.</p>
                     <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                            <FormLabel className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Nombre
                            </FormLabel>
                            <FormControl>
                                <Input placeholder="Tu nombre completo" {...field} />
                            </FormControl>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                    <FormField
                        control={form.control}
                        name="client_phone"
                        render={({ field }) => (
                            <FormItem>
                            <FormLabel className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                Teléfono de Contacto
                            </FormLabel>
                            <FormControl>
                                <Input placeholder="Tu número de teléfono" {...field} />
                            </FormControl>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                </div>
            )}

            <div className="!mt-8 text-right font-bold text-2xl text-primary">
                {isPriceAvailable ? `Precio Estimado: $${estimatedPrice.toLocaleString('es-MX')}` : ''}
            </div>

            <Button type="submit" className="w-full">
              {isManualQuoteNeeded ? "Solicitar Cotización" : "Agregar a la lista"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </>
  );
}
